// Phase MHM-6.D · MHM-only inbound parcels screen.
// Lists orders handed over to caller's main hub. Polls every 15s.
import { useEffect, useState, useCallback } from "react";
import { Camera, Eye, Inbox, RefreshCw, X, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import MHMSubNav from "../../components/hub/MHMSubNav";
import StatusBadge from "../../components/hub/StatusBadge";
import { useOrderDrawer } from "../../components/hub/OrderDrawerContext";
import HubHandoverVerify from "../../components/hub/HubHandoverVerify";
import HubOpsPanel from "../../components/hub/HubOpsPanel";

function relative(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  const rem = m - h * 60;
  return rem ? `${h}h ${rem}m ago` : `${h}h ago`;
}

function ReceiveModal({ tid, onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const onPick = (e) => setFiles(Array.from(e.target.files || []));
  const submit = async () => {
    if (files.length === 0) {
      toast.error("At least one photo is required");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f, f.name));
      if (remarks) fd.append("remarks", remarks);
      await api.post(`/hub/inbound-parcels/${tid}/receive`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Parcel received");
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e.userMessage || e?.response?.data?.detail || "Receive failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button aria-label="Close" onClick={onClose}
              className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm"
              data-testid="receive-modal-backdrop" />
      <div role="dialog" aria-modal="true"
           data-testid="receive-modal"
           className="fixed inset-x-3 bottom-3 top-12 sm:inset-auto sm:top-1/2 sm:left-1/2
                      sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[420px]
                      bg-white rounded-2xl shadow-2xl z-50 flex flex-col">
        <div className="border-b border-slate-200 px-4 py-3 flex items-center gap-2">
          <Inbox className="h-4 w-4 text-teal-700" />
          <h3 className="font-semibold text-slate-800 flex-1">Receive {tid}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"
                  data-testid="receive-modal-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Received-proof photo(s) <span className="text-rose-600">*</span>
            </span>
            <input type="file" accept="image/*" capture="environment" multiple
                   onChange={onPick}
                   data-testid="receive-modal-file-input"
                   className="mt-1 block w-full text-sm" />
          </label>
          {files.length > 0 && (
            <div className="grid grid-cols-3 gap-2" data-testid="receive-modal-previews">
              {files.map((f, i) => (
                <img key={i} src={URL.createObjectURL(f)} alt={`preview-${i}`}
                     className="aspect-square object-cover rounded-lg border border-slate-200" />
              ))}
            </div>
          )}
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Remarks (optional, 200 chars)
            </span>
            <textarea value={remarks} maxLength={200}
                      onChange={(e) => setRemarks(e.target.value)}
                      data-testid="receive-modal-remarks"
                      className="mt-1 block w-full text-sm border border-slate-200
                                 rounded-lg p-2 min-h-[64px]" />
            <span className="text-[10px] text-slate-400">{remarks.length}/200</span>
          </label>
        </div>
        <div className="border-t border-slate-200 px-4 py-3 flex gap-2">
          <button onClick={onClose}
                  className="flex-1 text-sm py-2.5 border border-slate-200 rounded-lg"
                  data-testid="receive-modal-cancel">Cancel</button>
          <button onClick={submit} disabled={busy || files.length === 0}
                  data-testid="receive-modal-submit"
                  className="flex-1 text-sm py-2.5 bg-teal-600 hover:bg-teal-700
                             text-white rounded-lg font-semibold disabled:opacity-50
                             inline-flex items-center justify-center gap-1">
            <Upload className="h-4 w-4" />
            {busy ? "Receiving…" : "Receive Parcel"}
          </button>
        </div>
      </div>
    </>
  );
}

export default function InboundParcels() {
  const [data, setData] = useState({ rows: [], total_count: 0, delayed_count: 0 });
  const [loading, setLoading] = useState(true);
  const [delayedOnly, setDelayedOnly] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("");
  const [receiving, setReceiving] = useState(null);
  const { openOrder } = useOrderDrawer();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (delayedOnly) params.set("delayed_only", "true");
      if (sourceFilter) params.set("source_micro_hub_id", sourceFilter);
      const { data } = await api.get(`/hub/inbound-parcels?${params.toString()}`);
      setData(data || { rows: [], total_count: 0, delayed_count: 0 });
    } catch (e) {
      toast.error(e.userMessage || "Failed to load inbound parcels");
    } finally {
      setLoading(false);
    }
  }, [delayedOnly, sourceFilter]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  // Source filter options derived from the response so we don't depend on
  // a separate hubs endpoint.
  const sources = Array.from(new Set(
    (data.rows || []).map((r) => r.source_micro_hub_id).filter(Boolean)
  ));

  const badgeClass = data.delayed_count > 0
    ? "bg-red-100 text-red-800 border-red-200"
    : "bg-slate-100 text-slate-700 border-slate-200";

  return (
    <div data-testid="mhm-inbound-page" className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <MHMSubNav />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-5 space-y-4">
        {/* Agent → Hub handover OTP verification */}
        <HubHandoverVerify onVerified={load} />
        {/* Operational hub transfers + dispatch + line-haul */}
        <HubOpsPanel />
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Inbox className="h-5 w-5 text-teal-700" />
            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">
              Inbound Parcels — {data.main_hub_id || ""}
            </h1>
            <span data-testid="inbound-count-badge"
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${badgeClass}`}>
              {data.total_count} pending
              {data.delayed_count > 0 && (
                <span className="ml-1.5 text-red-700">· {data.delayed_count} delayed</span>
              )}
            </span>
          </div>
          <button onClick={load}
                  data-testid="inbound-refresh"
                  className="inline-flex items-center gap-1 text-sm px-3 py-1.5
                             border border-slate-200 rounded-lg hover:bg-slate-100">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-slate-800
                        border border-slate-200 dark:border-slate-700 rounded-xl p-3">
          <label className="text-xs text-slate-600 dark:text-slate-300">Source micro hub
            <select value={sourceFilter}
                    onChange={(e) => setSourceFilter(e.target.value)}
                    data-testid="inbound-filter-source"
                    className="ml-2 text-sm border border-slate-200 rounded-lg px-2 py-1">
              <option value="">All</option>
              {sources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-600 dark:text-slate-300 flex items-center gap-1">
            <input type="checkbox" checked={delayedOnly}
                   onChange={(e) => setDelayedOnly(e.target.checked)}
                   data-testid="inbound-filter-delayed" />
            Delayed only (&gt; 120 min)
          </label>
        </div>

        {/* List */}
        {loading && data.rows.length === 0 && (
          <p className="text-slate-500 text-sm py-12 text-center"
             data-testid="inbound-loading">Loading…</p>
        )}
        {!loading && data.rows.length === 0 && (
          <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl"
               data-testid="inbound-empty">
            <Inbox className="h-12 w-12 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">No inbound parcels right now.</p>
          </div>
        )}
        {data.rows.length > 0 && (
          <div className="grid gap-3" data-testid="inbound-list">
            {data.rows.map((row) => (
              <div key={row.tracking_id}
                   data-testid={`inbound-row-${row.tracking_id}`}
                   data-tid={row.tracking_id}
                   className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                              rounded-xl p-4 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-slate-800 dark:text-slate-100">
                      {row.tracking_id}
                    </span>
                    <StatusBadge status="handed_over_to_main_hub" />
                    {row.is_delayed && <StatusBadge status="delayed" />}
                    {row.priority && row.priority !== "normal" && (
                      <span className="text-[10px] uppercase text-amber-700 font-bold">
                        {row.priority}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 space-x-2">
                    <span>{row.customer_name}</span>
                    <span>·</span>
                    <span>{row.items_count} item(s)</span>
                    <span>·</span>
                    <span>from <span className="font-mono">{row.source_micro_hub_id}</span>
                      {row.source_micro_hub_name && ` (${row.source_micro_hub_name})`}</span>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    <span title={row.handed_over_at}>
                      Handed over: {relative(row.handed_over_at)}
                      {row.elapsed_minutes_since_handover != null && (
                        <span className="text-slate-400"> ({row.elapsed_minutes_since_handover}m)</span>
                      )}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Camera className="h-3 w-3" /> {row.handover_proof_count} proof(s)
                    </span>
                    {row.is_delayed && (
                      <span className="inline-flex items-center gap-1 text-red-700">
                        <AlertTriangle className="h-3 w-3" /> Delayed
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openOrder(row.tracking_id)}
                          data-testid={`inbound-preview-${row.tracking_id}`}
                          className="inline-flex items-center gap-1 text-sm px-3 py-2
                                     border border-slate-200 rounded-lg hover:bg-slate-50">
                    <Eye className="h-3.5 w-3.5" />
                    Open Drawer
                  </button>
                  <button onClick={() => setReceiving(row.tracking_id)}
                          data-testid={`inbound-receive-${row.tracking_id}`}
                          className="inline-flex items-center gap-1 text-sm px-3 py-2
                                     bg-teal-600 hover:bg-teal-700 text-white rounded-lg
                                     font-semibold">
                    <Upload className="h-3.5 w-3.5" />
                    Receive Parcel
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {receiving && (
        <ReceiveModal tid={receiving}
                       onClose={() => setReceiving(null)}
                       onDone={load} />
      )}
    </div>
  );
}
